import { useAbortableConfirm } from "../lib/useAbortableConfirm";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { usePlayerProfileScope } from "../lib/usePlayerProfileScope";
import {
  api,
  ApiError,
  type ClubBookingRules,
  type ClubReservationInfo,
  type PlayerAvailabilityCourt,
  type PlayerClubBrief,
} from "../lib/api";
import { formatDate } from "../lib/dateFormat";

const RESERVATION_STATUS_LABELS: Record<string, string> = {
  active: "Activa",
  cancelled: "Cancelada",
  cancelled_late: "Cancelada tarde",
  no_show: "No se presentó",
};

const EMPTY_RULES: ClubBookingRules = {
  max_advance_days: null,
  min_advance_hours: null,
  max_active_reservations: null,
  max_per_day: null,
  max_per_week: null,
  max_consecutive_blocks: null,
  cancel_hours: null,
  prime_time_json: null,
  prime_max_per_week: null,
};

function todayIso(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

function parseIsoDate(dateIso: string): Date {
  const [year, month, day] = dateIso.split("-").map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
}

function toIsoDate(dateValue: Date): string {
  const month = String(dateValue.getMonth() + 1).padStart(2, "0");
  const day = String(dateValue.getDate()).padStart(2, "0");
  return `${dateValue.getFullYear()}-${month}-${day}`;
}

function addDaysIso(dateIso: string, days: number): string {
  const dateValue = parseIsoDate(dateIso);
  dateValue.setDate(dateValue.getDate() + days);
  return toIsoDate(dateValue);
}

function formatDateLabel(dateIso: string | null): string {
  if (!dateIso) return "—";
  const dateValue = parseIsoDate(dateIso);
  return dateValue.toLocaleDateString("es-MX", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

function shortDayLabel(dateIso: string): string {
  const dateValue = parseIsoDate(dateIso);
  return dateValue.toLocaleDateString("es-MX", { weekday: "short", day: "numeric" });
}

function formatTimeLabel(time: string): string {
  return time.slice(0, 5);
}

function limitText(value: number | null | undefined, unit: string): string {
  return value == null ? "Sin límite" : `${value} ${unit}${value === 1 ? "" : "s"}`;
}

export default function PlayerClubReservationsPage() {
  const { token } = useAuth();
  const { clubId: clubIdParam } = useParams();
  const clubId = Number(clubIdParam);
  const { askConfirm } = useAbortableConfirm([clubId]);
  const [club, setClub] = useState<PlayerClubBrief | null>(null);
  const [date, setDate] = useState(todayIso());
  const [courts, setCourts] = useState<PlayerAvailabilityCourt[]>([]);
  const [bookingRules, setBookingRules] = useState<ClubBookingRules>(EMPTY_RULES);
  const [reservations, setReservations] = useState<ClubReservationInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // Cuenta familiar: ¿el hijo elegido es socio de este club?
  const scope = usePlayerProfileScope();
  const scopeProfileId = scope.profileId;

  const loadClub = useCallback(async () => {
    if (!token || !Number.isFinite(clubId) || !scope.ready) return;
    setError(null);
    try {
      const resp = await api.clubs.playerClubs(token, scopeProfileId);
      const owned = resp.data.my_clubs ?? resp.data.clubs ?? [];
      const selected = owned.find((candidate) => candidate.id === clubId) ?? null;
      setClub(selected);
      if (!selected) {
        setError("No eres socio activo de este club o la solicitud aún no fue aprobada.");
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No pudimos cargar tu club.");
    } finally {
      setLoading(false);
    }
  }, [token, clubId, scope.ready, scopeProfileId]);

  const refreshReservations = useCallback(async () => {
    if (!token || !club) return;
    try {
      const [availResp, mineResp] = await Promise.all([
        api.clubs.playerAvailability(token, club.id, date, scopeProfileId),
        api.clubs.playerMyReservations(token, club.id, scopeProfileId),
      ]);
      setCourts(availResp.data.courts);
      setBookingRules(availResp.data.rules ?? EMPTY_RULES);
      setReservations(mineResp.data.reservations);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No pudimos cargar la disponibilidad.");
    }
  }, [token, club, date, scopeProfileId]);

  useEffect(() => {
    void loadClub();
  }, [loadClub]);

  useEffect(() => {
    void refreshReservations();
  }, [refreshReservations]);

  const activeReservations = reservations.filter((reservation) => reservation.status === "active");
  const history = reservations.filter((reservation) => reservation.status !== "active");
  const days = useMemo(() => Array.from({ length: 7 }, (_, index) => addDaysIso(todayIso(), index)), []);
  const calendarRows = useMemo(() => {
    const times = new Set<string>();
    courts.forEach((court) => court.slots.forEach((slot) => times.add(slot.start_time)));
    return Array.from(times).sort((a, b) => a.localeCompare(b));
  }, [courts]);
  const slotLookup = useMemo(() => {
    const lookup = new Map<string, { start_time: string; end_time: string; available: boolean }>();
    courts.forEach((court) => {
      court.slots.forEach((slot) => lookup.set(`${court.id}-${slot.start_time}`, slot));
    });
    return lookup;
  }, [courts]);

  async function handleBook(courtId: number, courtName: string, startTime: string) {
    if (!token || !club) return;
    const confirmed = await askConfirm(`¿Reservar ${courtName} el ${formatDateLabel(date)} a las ${startTime}?`, { acceptLabel: "Sí, reservar" });
    if (!confirmed) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await api.clubs.playerBook(token, club.id, { court_id: courtId, date, start_time: startTime }, scopeProfileId);
      setMessage(`Reserva confirmada: ${courtName} a las ${startTime}.`);
      await refreshReservations();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo reservar.");
    } finally {
      setBusy(false);
    }
  }

  async function handleCancel(reservation: ClubReservationInfo) {
    if (!token) return;
    const confirmed = await askConfirm(`¿Cancelar tu reserva del ${formatDateLabel(reservation.date)} a las ${reservation.start_time}?`, { acceptLabel: "Sí, cancelar mi reserva", tone: "danger" });
    if (!confirmed) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const resp = await api.clubs.playerCancelReservation(token, reservation.id);
      setMessage(resp.data.late ? "Reserva cancelada tarde." : "Reserva cancelada.");
      await refreshReservations();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo cancelar.");
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return <section className="card"><p className="muted">Cargando reservas del club…</p></section>;
  }

  if (!club) {
    return (
      <section className="card">
        <p className="eyebrow">Reservas del club</p>
        <h1>Club no disponible</h1>
        {error && <p className="form-error" role="alert">{error}</p>}
        <Link className="btn" to="/player/clubs">Volver a Clubes</Link>
      </section>
    );
  }

  return (
    <div className="content-stack player-club-reservations-page">
      <section className="card player-club-availability-card">
        <div className="card__header-row player-club-availability-header">
          <div>
            <p className="eyebrow">Reservas · {club.name}</p>
            <h2 className="card__title">Disponibilidad — {formatDateLabel(date)}</h2>
            <p className="card__subtitle">Toca una celda libre para reservar cancha.</p>
          </div>
          <div className="player-club-availability-actions">
            <Link className="btn btn--compact btn--ghost" to="/player/clubs">← Cambiar club</Link>
            <label className="schedule-toolbar__field">
              <span className="schedule-toolbar__label">Fecha</span>
              <input type="date" value={date} disabled={busy} onChange={(e) => setDate(e.target.value)} />
            </label>
          </div>
        </div>
        {error && <p className="form-error" role="alert">{error}</p>}
        {message && <p className="form-message">{message}</p>}
        <div className="player-club-day-strip" aria-label="Días disponibles">
          {days.map((day) => (
            <button key={day} type="button" className={day === date ? "is-active" : ""} onClick={() => setDate(day)} disabled={busy}>
              <strong>{day === todayIso() ? "Hoy" : shortDayLabel(day)}</strong>
              <span>{formatDate(day).slice(0, 5)}</span>
            </button>
          ))}
        </div>
        <div className="player-club-rules-note">
          Reglas: {limitText(bookingRules.max_active_reservations, "reserva activa")} · cancelar con {bookingRules.cancel_hours ?? "—"} h.
        </div>
        {courts.length === 0 ? (
          <p className="muted">El club no abre este día.</p>
        ) : (
          <div className="schedule-calendar__matrix-wrap player-club-calendar-wrap">
            <div className="schedule-calendar__matrix player-club-calendar-matrix" style={{ gridTemplateColumns: `minmax(54px, 64px) repeat(${courts.length}, minmax(104px, 112px))` }}>
              <div className="schedule-calendar__corner">Hora</div>
              {courts.map((court) => <div key={court.id} className="schedule-calendar__court-head">{court.name}<small>{court.surface}</small></div>)}
              {calendarRows.map((time) => (
                <div className="schedule-calendar__row" key={time}>
                  <div className="schedule-calendar__time">{formatTimeLabel(time)}</div>
                  {courts.map((court) => {
                    const slot = slotLookup.get(`${court.id}-${time}`);
                    const available = Boolean(slot?.available);
                    return (
                      <button key={`${court.id}-${time}`} type="button" className={`schedule-calendar__slot player-club-calendar-slot${available ? " is-free" : " is-busy"}`} disabled={!available || busy || !slot} onClick={() => slot && handleBook(court.id, court.name, slot.start_time)}>
                        <span><strong>{formatTimeLabel(time)}</strong><small>{available ? "libre" : "ocupado"}</small></span>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      <section className="card">
        <h2 className="card__title">Mis reservas</h2>
        {activeReservations.length === 0 ? <p className="muted">No tienes reservas activas.</p> : (
          <ul className="player-club-reservations">
            {activeReservations.map((reservation) => (
              <li key={reservation.id} className="player-club-reservation">
                <div><strong>{reservation.court_name ?? `Cancha ${reservation.court_id}`}</strong><span className="muted"> — {formatDateLabel(reservation.date)}, {reservation.start_time}–{reservation.end_time}</span></div>
                <button type="button" className="btn btn--compact btn--ghost" disabled={busy} onClick={() => handleCancel(reservation)}>Cancelar</button>
              </li>
            ))}
          </ul>
        )}
        {history.length > 0 && <p className="muted">Historial: {history.map((reservation) => RESERVATION_STATUS_LABELS[reservation.status] ?? reservation.status).join(", ")}</p>}
      </section>
    </div>
  );
}
